<script setup lang="ts">
import { computed } from 'vue';
import { useInjectGameEngine } from '@/composables/useGameEngine';
import { useInjectSheetMusic } from '@/composables/useSheetMusic';
import { useGameChromeVisibility } from '@/composables/useGameChromeVisibility';
import GameAreaHighScoreEligibilityIndicator from './GameAreaHighScoreEligibilityIndicator.vue';
import ProgressBar from './ProgressBar.vue';
import CopyIconButton from './shared/CopyIconButton.vue';
import type { Id } from '../../convex/_generated/dataModel';

const props = withDefaults(defineProps<{
  pieceTempo?: number;
  canSeekProgress?: boolean;
  musicalPieceId?: Id<'musicalPieces'>;
  /** Library name of the piece. Absent in Quick Start, where the score is an ad-hoc upload. */
  pieceName?: string;
  /** Classroom room code. Absent outside a classroom, so the slot never toggles mid-session. */
  roomCode?: string;
}>(), {
  canSeekProgress: true,
});

const emit = defineEmits<{
  'seek': [data: { timeMs: number; wasPlaying: boolean }];
}>();

const gameEngine = useInjectGameEngine();
const sheetMusic = useInjectSheetMusic();
const chrome = useGameChromeVisibility();

const displayTitle = computed(() => props.pieceName || sheetMusic.songTitle.value || 'Untitled');
const displayTempo = computed(() => props.pieceTempo ?? sheetMusic.initialBpm.value);

const handleProgressBarSeek = (progress: number) => {
  if (!props.canSeekProgress) return;

  const wasPlaying = gameEngine.isPlaying.value;
  if (wasPlaying) {
    gameEngine.stopPlayback();
  }

  const newTimeMs = (progress / 100) * sheetMusic.songDuration.value;
  gameEngine.seekTo(newTimeMs);
  emit('seek', { timeMs: newTimeMs, wasPlaying });
};
</script>

<template>
  <div
    class="game-status-bar w-full p-4 pt-6 sm:px-6 lg:px-8"
    :class="{ 'game-status-bar--hidden': !chrome.visible.value }"
  >
    <div class="flex justify-between items-center">
      <div class="flex items-center min-w-0">
        <div class="game-header-left-slot">
          <slot name="header-left" />
        </div>
        <div class="flex flex-col min-w-0">
          <span class="text-sm text-text-secondary">Song</span>
          <span class="font-semibold text-text-primary truncate block">{{ displayTitle }}</span>
        </div>
      </div>

      <!-- Centre slot, previously the sheet music / arcade switch. -->
      <div
        v-if="props.roomCode"
        class="room-code-display"
      >
        <span class="room-code-label">Code</span>
        <div class="room-code-row">
          <span class="room-code-value">{{ props.roomCode }}</span>
          <CopyIconButton
            :text="props.roomCode"
            subject="room code"
          />
        </div>
      </div>

      <div class="flex space-x-4 pr-12 items-center">
        <GameAreaHighScoreEligibilityIndicator v-if="props.musicalPieceId" />
        <div class="flex flex-col items-end min-w-16">
          <span class="text-sm text-text-secondary">Tempo</span>
          <span class="text-text-primary">{{ displayTempo }} BPM</span>
        </div>
        <div class="flex flex-col items-end min-w-16">
          <span class="text-sm text-text-secondary">Score</span>
          <span class="text-text-primary">{{ gameEngine.score.value }}</span>
        </div>
      </div>
    </div>

    <!-- Progress bar -->
    <div class="w-full h-4 mt-4">
      <ProgressBar
        :value="gameEngine.progress.value"
        :interactive="props.canSeekProgress"
        @seek="handleProgressBarSeek"
      />
    </div>
  </div>
</template>

<style scoped>
/*
 * Floats over the full-bleed sheet music (see GameArea.vue) instead of
 * pushing it down, so the sheet gets the whole screen height. The scrim is
 * built from the theme's own --color-secondary (so it's a light haze in
 * light mode, a dark haze in dark mode) fading to transparent — text stays
 * the normal theme-aware --color-text-* colors, so contrast is automatically
 * right in both themes instead of forcing light-on-dark like a video player.
 * Opacity + translate together (not just opacity) so it's fully out of the
 * way once hidden, matching the mobile YouTube player.
 */
.game-status-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  /* Below MainNav's fixed header (z-index 20): while sliding out on dismiss
     this rises up past the padding reserved for the header (see App.vue's
     pt-16) and must tuck behind it, not paint over it. */
  z-index: 15;
  background: linear-gradient(
    to bottom,
    color-mix(in srgb, var(--color-secondary) 92%, transparent),
    color-mix(in srgb, var(--color-secondary) 55%, transparent) 60%,
    transparent
  );
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.game-status-bar--hidden {
  opacity: 0;
  transform: translateY(-100%);
  pointer-events: none;
}

.game-header-left-slot {
  min-width: 40px;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.room-code-display {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  line-height: 1;
}

.room-code-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.room-code-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.25em;
  color: var(--color-text-secondary);
  text-transform: uppercase;
}

.room-code-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, 'SF Mono', monospace;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: var(--color-text-primary);
  user-select: all;
}
</style>
